import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';

import { useSearchMoviesAndShowsQuery } from '../actions/tmdbApiSlice';
import PosterCard from '../components/PosterCard';

const placeholderImageUrl = 'https://placehold.co/300x450.png?text=No+Poster';

function SearchedScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('q');
  const { data, error, isFetching } = useSearchMoviesAndShowsQuery(searchQuery);

  const handleSuggestionClick = (item) => {
    let year = '';
    if (item.media_type === 'movie' && item.release_date) {
      year = item.release_date.split('-')[0];
    } else if (item.media_type === 'tv' && item.first_air_date) {
      year = item.first_air_date.split('-')[0];
    }
    const titleWithYear = year
      ? `${item.title || item.name} ${year}`
      : item.title || item.name;
    const idParam = item.media_type === 'tv' ? 'tid' : 'mid';
    navigate(`/details/?q=${titleWithYear}&${idParam}=${item.id}`);
  };

  if (error) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Container>
    );
  }

  const movieList = data?.results?.slice().sort((a, b) => {
    const dateA = a.release_date || a.first_air_date;
    const dateB = b.release_date || b.first_air_date;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return new Date(dateB) - new Date(dateA);
  });

  return (
    <>
      <Helmet>
        <title>{searchQuery} | Search results</title>
      </Helmet>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Search results
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            {searchQuery}
          </Typography>
          <Typography color="text.secondary">
            {movieList ? `${movieList.length} matches` : ''}
          </Typography>
        </Box>

        {isFetching ? (
          <Grid container spacing={2}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
                <Skeleton variant="rounded" sx={{ width: '100%', aspectRatio: '2 / 3', borderRadius: 2 }} />
                <Skeleton sx={{ mt: 1, width: '80%' }} />
              </Grid>
            ))}
          </Grid>
        ) : movieList && movieList.length > 0 ? (
          <Grid container spacing={2}>
            {movieList.map((item) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={`${item.media_type}-${item.id}`}>
                <PosterCard
                  src={
                    item.poster_path
                      ? `https://image.tmdb.org/t/p/w342${item.poster_path}`
                      : placeholderImageUrl
                  }
                  title={item.title || item.name}
                  subtitle={item.release_date || item.first_air_date || ''}
                  badge={
                    item.vote_average
                      ? `★ ${(Math.floor(item.vote_average * 10) / 10).toFixed(1)}`
                      : undefined
                  }
                  onClick={() => handleSuggestionClick(item)}
                  width="100%"
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: (t) => `1px dashed ${t.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No results
            </Typography>
            <Typography color="text.secondary">
              Try a different search term.
            </Typography>
          </Paper>
        )}
      </Container>
    </>
  );
}

export default SearchedScreen;
